/** Login Lit + FormComponent (hexagonal entrypoint). */
import { TemplateResult } from 'lit';
import { LitComponent } from '../../../shared/ui/infrastructure/drivers/ui/components/LitComponent.ts';
import { FormComponent } from '../../../shared/ui/infrastructure/drivers/ui/components/FormComponent.ts';
import type { FormFieldProps } from '../../../shared/ui/infrastructure/drivers/ui/components/FormComponent.ts';
import type { CoreShellPorts } from '@src/modules/shared/routing/domain/CoreShellPorts.ts';
import { getErrorMessage } from '@core/domain/utils/errorHandler.ts';
import { joinMissingFieldLabelsEs } from '@core/domain/utils/joinMissingFieldLabelsEs.ts';
import { notifyAuthLoginSuccess } from '@src/modules/shared/bootstrap/infrastructure/applicationShellBootstrap.ts';
import { loginComponentTemplate } from './loginComponentTemplate.ts';

const LOGIN_FORM_SUBMIT_BUTTON_CLASS =
  'w-full min-h-12 rounded-md font-medium shadow-[var(--md-elevation-1)] transition-opacity hover:opacity-95 active:opacity-90 disabled:cursor-not-allowed disabled:opacity-40 text-[color:var(--md-sys-color-on-primary)] bg-[color:var(--md-sys-color-primary)] py-3 px-6';

/** Returns the base properties for the login form. */
function loginFormBaseProps(
  onSubmit: (formData: FormData) => void | Promise<void>,
  isLoading: boolean,
): {
  fields: FormFieldProps[];
  submitLabel: string;
  onSubmit: (formData: FormData) => void | Promise<void>;
  layout?: string;
  submitButtonClass?: string;
} {
  return {
    fields: [
      {
        name: 'email',
        label: 'Correo electrónico o usuario',
        type: 'text',
        required: false,
        placeholder: 'Ingrese su correo o usuario',
      },
      {
        name: 'password',
        label: 'Contraseña',
        type: 'password',
        required: false,
        placeholder: 'Ingrese su contraseña',
      },
    ],
    onSubmit,
    submitLabel: isLoading ? 'Entrando…' : 'Entrar',
    layout: 'vertical',
    submitButtonClass: LOGIN_FORM_SUBMIT_BUTTON_CLASS,
  };
}

export class LoginComponent extends LitComponent {
  /** Generic shell only (login does not use PE / redemption). */
  shellPorts!: Pick<CoreShellPorts, 'shellUi' | 'auth' | 'telemetry'>;

  private errorMessage: string = '';
  private formWireAttempts = 0;
  private static readonly maxFormWireAttempts = 60;

  /** Wire the nested form after the shadow tree (and form-component) exist. */
  connectedCallback(): void {
    super.connectedCallback();
    this.scheduleLoginFormWire();
  }

  /** Renders the component and re-syncs form props (submit label, handler). */
  protected render(): void {
    super.render();
    this.scheduleLoginFormWire();
  }

  protected update(): void {
    super.update();
    this.scheduleLoginFormWire();
  }

  private scheduleLoginFormWire(): void {
    this.formWireAttempts = 0;
    queueMicrotask(() => this.wireLoginForm());
  }

  /** Returns the login component template. */
  protected getTemplate(): TemplateResult {
    return loginComponentTemplate({
      appTitle: this.shellPorts.shellUi.getAppDisplayTitle(),
      errorMessage: this.errorMessage,
      isLoading: this.isLoading,
    });
  }

  /** Binds submit handler and fields on nested form-component (retries until its shadow exists). */
  private wireLoginForm(): void {
    if (!this.isConnected || !this.shadow) return;
    const form = this.shadow.getElementById('login-form') as FormComponent | null;
    if (!form?.shadowRoot) {
      if (this.formWireAttempts < LoginComponent.maxFormWireAttempts) {
        this.formWireAttempts += 1;
        requestAnimationFrame(() => this.wireLoginForm());
      }
      return;
    }
    this.formWireAttempts = 0;
    form.setProps(loginFormBaseProps(this.handleSubmit, this.isLoading));
  }

  /** Handles the login form submission. */
  private readonly handleSubmit = async (formData: FormData): Promise<void> => {
    this.errorMessage = '';
    const usernameOrEmail = (formData.get('email') as string) || '';
    const password = (formData.get('password') as string) || '';
    const faltan: string[] = [];
    if (!usernameOrEmail.trim()) faltan.push('Correo electrónico o usuario');
    if (!password) faltan.push('Contraseña');
    if (faltan.length > 0) {
      this.errorMessage = joinMissingFieldLabelsEs(faltan);
      this.update();
      return;
    }

    await this.executeWithLoading(
      async () => {
        await this.shellPorts.auth.login(usernameOrEmail, password);
        if (!(await this.shellPorts.auth.isAuthenticated())) {
          throw new Error('No se pudo guardar la sesión en el dispositivo.');
        }
        this.shellPorts.telemetry.trackEvent('auth.login.success', {});

        // Resume shell bootstrap in-page (router was not started on `/login`).
        notifyAuthLoginSuccess();
        return true;
      },
      () => {
        this.isLoading = true;
      },
      () => {
        this.isLoading = false;
      },
      (error: Error) => {
        this.isLoading = false;
        this.errorMessage = getErrorMessage(error) || 'Failed to sign in';
        this.shellPorts.telemetry.trackEvent('auth.login.failure', {
          message: this.errorMessage,
        });
      }
    );
  };
}

customElements.define('login-component', LoginComponent);
